import { useState, useEffect, useRef, lazy, Suspense } from 'react'
import TextStatic from '@/components/app/TextStatic/TextStatic'
import type { Project } from '@/store/projects/projectsSlice'
import css from './WorkspaceProjectDescription.module.css'

// Lazy (not via LazyTextEditor) so the static read view can be the Suspense fallback —
// the text stays in place while the editor chunk inits instead of collapsing to empty.
// Read view by default means a Plate editor is NOT mounted on every project open; that
// synchronous mount was the project-open cost this avoids (SCD-874, mirrors SCD-867).
const TextEditor = lazy(() => import('@/components/app/TextEditor/TextEditor'))

const PLACEHOLDER = 'Write a description, brief...'

interface WorkspaceProjectDescriptionProps {
    project: Project
    onUpdateDescription: (description: string) => void
    onFileUpload?: (file: File) => Promise<{ id: string; url: string }>
}

const WorkspaceProjectDescription = ({ project, onUpdateDescription, onFileUpload }: WorkspaceProjectDescriptionProps) => {
    const [editing, setEditing] = useState(false)
    // Mount the editor (hidden) once the pointer enters, so spinning up the Plate instance
    // happens during the hover→click gap instead of adding lag to the click itself.
    const [prewarm, setPrewarm] = useState(false)
    // Viewport point of the click that started editing, so the editor places the caret there
    // instead of jumping to the end (the static read view and editor lay out identically).
    const [focusPoint, setFocusPoint] = useState<{ x: number; y: number } | null>(null)
    // Mirror the prop so the static view shows just-typed content immediately after blur,
    // without waiting for the Firestore round-trip to flow back through props.
    const [localValue, setLocalValue] = useState(project.description)

    // Adopt the prop ONLY on a genuine external change (e.g. another tab edited it) — not on
    // the editing→false toggle, which would briefly flash the pre-save content while our own
    // write is still round-tripping through Firestore.
    const lastDescRef = useRef(project.description)
    useEffect(() => {
        if (JSON.stringify(project.description) === JSON.stringify(lastDescRef.current)) return
        lastDescRef.current = project.description
        if (!editing) setLocalValue(project.description)
    }, [project.description, editing])

    const startEditing = (e: React.MouseEvent) => {
        // Only skip entering edit for elements with their own click action in the read view:
        // links / the file download link (`a`), buttons, and the Figma preview (lightbox).
        if ((e.target as HTMLElement).closest('a, button, [data-lightbox-trigger]')) return
        setFocusPoint({ x: e.clientX, y: e.clientY })
        setEditing(true)
    }

    const stopEditing = () => {
        setFocusPoint(null)
        setEditing(false)
    }

    const handleSave = (value: unknown) => {
        onUpdateDescription(value as string)
    }

    // Dragging a file onto the (read-only) description enters edit mode, so the editor's own
    // capture-phase drop handler does the upload/insert — no duplicated logic.
    const isFileDrag = (e: React.DragEvent) => e.dataTransfer.types.includes('Files')
    const handleDragEnter = (e: React.DragEvent) => {
        if (editing || !onFileUpload || !isFileDrag(e)) return
        setPrewarm(true)
        setFocusPoint(null)
        setEditing(true)
    }
    const handleDragOver = (e: React.DragEvent) => {
        if (isFileDrag(e)) e.preventDefault() // keep the drag valid so a drop fires
    }
    const handleDrop = (e: React.DragEvent) => {
        // Safety net only: the editor normally consumes the drop first. If it hadn't mounted
        // yet, stop the browser from navigating to the dropped file.
        if (isFileDrag(e)) e.preventDefault()
    }

    // The read view. TextStatic renders the placeholder (as the editor's exact placeholder DOM)
    // when empty, so it doubles as read mode and as the editor's Suspense fallback — entering
    // edit on an empty description keeps the same placeholder element in place, no flicker.
    const renderReadView = () => <TextStatic value={localValue} placeholder={PLACEHOLDER} />

    const showEditor = prewarm || editing
    const handleContainerClick = editing ? undefined : startEditing
    // Hidden + out of flow while only pre-warming (not yet editing).
    const editorWrapClass = editing ? undefined : css.prewarm
    // Keep the read view in place while the editor chunk loads, only once actually editing.
    const editorFallback = editing ? renderReadView() : null

    return (
        <div className={css.content}>
            <div
                className={css.editor}
                onMouseEnter={() => setPrewarm(true)}
                onClick={handleContainerClick}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
            >
                {showEditor && (
                    // One editor instance: hidden while pre-warming, revealed on edit. `active`
                    // gates focus so the hidden instance doesn't steal focus before the click.
                    <div className={editorWrapClass}>
                        <Suspense fallback={editorFallback}>
                            <TextEditor
                                value={localValue}
                                placeholder={PLACEHOLDER}
                                autoFocus
                                active={editing}
                                focusAtPoint={focusPoint}
                                onSave={handleSave}
                                onChangeImmediate={setLocalValue}
                                onFileUpload={onFileUpload}
                                onBlur={stopEditing}
                            />
                        </Suspense>
                    </div>
                )}
                {!editing && renderReadView()}
            </div>
        </div>
    )
}

export default WorkspaceProjectDescription
